import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addRoomGroup'
  | 'getRoomGroup'
  | 'getRoomGroups'
  | 'updateRoomGroup';

export const useRoomGroups = () => {
  const [action, setAction] = useState<Action>('');
  const [roomGroup, setRoomGroup] = useState<RoomGroup | null>(null);
  const [roomGroups, setRoomGroups] = useState<RoomGroup[]>([]);
  const { loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    roomGroups: operatorRoomGroups,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<{ name: string }>('name');
  const BASE_URL = `${BASE_URL_MAP['Hospitality']}/${operatorId}/RoomGroups`;

  useEffect(() => {
    resetState();
  }, [operatorId]);

  useEffect(() => {
    if (response && !loading) {
      if (action === 'updateRoomGroup') {
        const mappedRoomGroup = mapRoomGroup(response);
        updateOperatorState(
          'roomGroups',
          [
            mappedRoomGroup,
            ...(operatorRoomGroups ?? []).filter(
              (roomGroup) => roomGroup.id !== mappedRoomGroup.id
            ),
          ].sort(sortAsc)
        );
      } else if (action === 'addRoomGroup') {
        const mappedRoomGroup = mapRoomGroup(response);

        updateOperatorState(
          'roomGroups',
          [mappedRoomGroup, ...(operatorRoomGroups ?? [])].sort(sortAsc)
        );
      } else if (action === 'getRoomGroup')
        setRoomGroup(mapRoomGroup(response));
      else if (action === 'getRoomGroups')
        setRoomGroups(
          response.map((roomGroup: ApiRoomGroup) => mapRoomGroup(roomGroup))
        );
    }
    // eslint-disable-next-line
  }, [loading, response]);

  const addRoomGroup = (roomGroup: CreateRoomGroup) => {
    setAction('addRoomGroup');
    const body = mapApiRoomGroup(roomGroup);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const getRoomGroup = (roomGroupId: string) => {
    setAction('getRoomGroup');
    makeRequest(`${BASE_URL}/${roomGroupId}`, 'get');
  };

  const getRoomGroups = () => {
    setAction('getRoomGroups');
    makeRequest(BASE_URL, 'get');
  };

  const updateRoomGroup = (roomGroup: RoomGroup) => {
    setAction('updateRoomGroup');
    const body: ApiCreateRoomGroup = sanitizeValue({
      Active: roomGroup.active,
      Name: roomGroup.name,
    });

    makeRequest(`${BASE_URL}/${roomGroup.id}`, 'put', body);
  };

  const resetState = () => {
    setAction('');
    setRoomGroup(null);
    setRoomGroups([]);
  };

  return {
    addRoomGroup,
    roomGroup,
    roomGroups,
    getRoomGroup,
    getRoomGroups,
    loading,
    updateRoomGroup,
  };
};

const mapApiRoomGroup = (roomGroup: CreateRoomGroup): ApiCreateRoomGroup => ({
  Active: roomGroup.active,
  Name: roomGroup.name,
});

export const mapRoomGroup = (roomGroup: ApiRoomGroup): RoomGroup => ({
  id: roomGroup.ID,
  active: roomGroup.Active,
  name: roomGroup.Name,
});
